import {
  Box,
  TextInput,
  Button,
  Stack,
  Grid,
  Text,
  Group,
  Skeleton,
  Tooltip,
  ActionIcon,
  Table,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { useClipboard } from '@mantine/hooks'
import {
  IconArrowsLeftRight,
  IconCircleCheckFilled,
  IconCopy,
  IconInfoCircle,
  IconLock,
  IconSquareArrowRight,
} from '@tabler/icons-react'
import countries from 'i18n-iso-countries'
import enLang from 'i18n-iso-countries/langs/en.json'
import { CircleFlag } from 'react-circle-flags'

import { LIGHT_BROWN, TEXT_ICONS_SECONDARY } from '~/widgets/shared/colors'
import classes from '~/widgets/TariffCalculator/components/TariffCalculatorResultsView.module.css'
import { TariffResult } from '~/widgets/TariffCalculator/hooks/useTariffCalculator'
import { TARIFF_CALC_STRINGS } from '~/widgets/TariffCalculator/strings'
import ResultsImageVariant from '~/widgets/TariffCalculator/tariff-results-image-variant.svg'

countries.registerLocale(enLang)

interface EmailFormValues {
  firstName: string
  lastName: string
  email: string
}

interface TariffCalculatorResultsViewVariantProps {
  isCalculating: boolean
  result: TariffResult | null
  product: string
  country: string | null
  emailForm: UseFormReturnType<EmailFormValues>
  isSubmittingEmail: boolean
  emailSubmitted: boolean
  onEmailSubmit: (values: EmailFormValues) => void
  onDemoClick: () => void
}

export function TariffCalculatorResultsView({
  isCalculating,
  result,
  product,
  country,
  emailForm,
  isSubmittingEmail,
  emailSubmitted,
  onEmailSubmit,
  onDemoClick,
}: TariffCalculatorResultsViewVariantProps) {
  const clipboard = useClipboard({ timeout: 2000 })
  const countryCode = country
    ? countries.getAlpha2Code(country, 'en')?.toLowerCase()
    : null

  return (
    <Box
      p="xl"
      bg="#f8f9fa"
      pos="relative"
      mih={750}
      style={{
        borderRadius: 12,
        boxShadow:
          '0px 0px 0.5px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
        overflow: 'visible',
      }}
    >
      <Stack gap="lg">
        {/* Subtitle */}
        <Group gap={4}>
          <Text fz={20} fw={600} lh="28px" c="#212529">
            Cost Analysis
          </Text>
          <Text fz={20} fw={600} lh="28px" c="#212529">
            {product}
          </Text>
        </Group>

        {/* Report Section */}
        <Grid gutter="lg" align="stretch">
          {/* Left side - Tariff Results */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="lg" h="100%">
              {/* Main Results Card */}
              <Box p="lg" className={classes.mainResultsCard}>
                {/* Tariff Rates */}
                <Box
                  style={{
                    flex: '0 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Stack gap="sm" style={{ flex: 1 }}>
                    <Box>
                      <Text fz={14} fw={600} c="#868e96" mb={4}>
                        Total Tariff Rate
                      </Text>
                      {isCalculating ? (
                        <Skeleton height={40} width={80} />
                      ) : (
                        <Text fz={32} fw={700} lh="40px" c="#b27444">
                          {result?.tariffRate && result?.tariffRate !== 'N/A'
                            ? `${result?.tariffRate}%`
                            : 'N/A'}
                        </Text>
                      )}
                    </Box>

                    {!isCalculating &&
                      result &&
                      country !== 'United States' &&
                      (result.baseRate || result.additionalRate) &&
                      (result.baseRate !== 'N/A' ||
                        result.additionalRate !== 'N/A') && (
                        <Group
                          gap="xl"
                          style={{ flexWrap: 'nowrap', marginTop: 'auto' }}
                        >
                          {result.baseRate &&
                            result.baseRate !== 'N/A' &&
                            result.baseRate.trim() !== '' && (
                              <Box>
                                <Text fz={14} fw={600} c="#868e96">
                                  Base Rate
                                </Text>
                                <Text fz={18} fw={600} c="#212529">
                                  {result.baseRate}%
                                </Text>
                              </Box>
                            )}
                          {result.additionalRate &&
                            result.additionalRate !== 'N/A' &&
                            result.additionalRate.trim() !== '' && (
                              <Box>
                                <Group gap={4} align="center">
                                  <Text fz={14} fw={600} c="#868e96">
                                    Additional Rate
                                  </Text>
                                  <Tooltip
                                    label={
                                      <Box
                                        style={{
                                          padding: '16px',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '16px',
                                        }}
                                      >
                                        <Text fz={14} fw={600} c="#212529">
                                          {
                                            TARIFF_CALC_STRINGS.whatIsAdditionalRate
                                          }
                                        </Text>
                                        <Text fz={14} fw={400} c="#212529">
                                          {
                                            TARIFF_CALC_STRINGS.additionalRateTooltip
                                          }
                                        </Text>
                                      </Box>
                                    }
                                    multiline
                                    maw={300}
                                    styles={{
                                      tooltip: {
                                        backgroundColor: 'white',
                                        boxShadow:
                                          '0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 3px 8px 0px rgba(0, 0, 0, 0.1)',
                                      },
                                    }}
                                  >
                                    <IconInfoCircle
                                      size={14}
                                      color={TEXT_ICONS_SECONDARY}
                                    />
                                  </Tooltip>
                                </Group>
                                <Text fz={18} fw={600} c="#212529">
                                  {result.additionalRate}%
                                </Text>
                              </Box>
                            )}
                        </Group>
                      )}
                  </Stack>
                </Box>

                {/* Vertical Divider */}
                <Box className={classes.verticalDivider} />

                {/* HTS Code and Country */}
                <Box
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Stack gap="sm" style={{ flex: 1 }}>
                    {/* HTS Code */}
                    <Box>
                      <Text fz={14} fw={600} c="#868e96">
                        HTS Code
                      </Text>
                      {isCalculating ? (
                        <Skeleton height={24} width={120} />
                      ) : (
                        <Group gap={8} align="center">
                          <Text fz={18} fw={600} c="#212529">
                            {result?.htsCode}
                          </Text>
                          {result?.htsCode && (
                            <Tooltip
                              label={
                                clipboard.copied
                                  ? TARIFF_CALC_STRINGS.copied
                                  : TARIFF_CALC_STRINGS.copyHTSCode
                              }
                            >
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() =>
                                  clipboard.copy(result.htsCode || '')
                                }
                                style={{
                                  color: clipboard.copied
                                    ? LIGHT_BROWN
                                    : TEXT_ICONS_SECONDARY,
                                  transition: 'color 0.2s',
                                }}
                              >
                                <IconCopy size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      )}
                    </Box>

                    {/* Sourced From */}
                    <Box style={{ marginTop: 'auto' }}>
                      <Text
                        fz={14}
                        fw={600}
                        c="#868e96"
                        mb={4}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Sourced From
                      </Text>
                      {isCalculating ? (
                        <Skeleton height={20} width={100} />
                      ) : (
                        <Group gap={8} align="center">
                          {countryCode && (
                            <CircleFlag countryCode={countryCode} height={20} />
                          )}
                          <Text fz={18} fw={600} c="#212529">
                            {country}
                          </Text>
                        </Group>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Grid.Col>

          {/* Right side */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Box
              p="lg"
              bg="white"
              h="100%"
              pos="relative"
              style={{ borderRadius: 8 }}
            >
              {/* Lock Icon */}
              <Box pos="absolute" top={16} style={{ right: 16 }}>
                <IconLock size={20} color={LIGHT_BROWN} />
              </Box>

              <Stack gap="sm">
                {/* Should Cost Section */}
                <Box>
                  <Text fz={14} fw={600} c="#868e96" mb={8}>
                    {TARIFF_CALC_STRINGS.shouldCost}
                  </Text>
                  <Group gap={4} align="baseline">
                    <Text fz={20} fw={700} c={LIGHT_BROWN}>
                      $
                    </Text>
                    <Text
                      fz={20}
                      fw={700}
                      style={{
                        background: `color-mix(in srgb, ${LIGHT_BROWN} 10%, transparent)`,
                        color: 'transparent',
                        filter: 'blur(5px)',
                      }}
                    >
                      33
                    </Text>
                    <Text
                      fz={20}
                      fw={700}
                      style={{
                        background: `linear-gradient(to right, #ffffff, color-mix(in srgb, ${LIGHT_BROWN} 60%, transparent))`,
                        backgroundClip: 'text',
                        color: 'transparent',
                        filter: 'blur(1px)',
                      }}
                    >
                      8
                    </Text>
                    <Text fz={14} fw={600} c={TEXT_ICONS_SECONDARY}>
                      {TARIFF_CALC_STRINGS.usd}
                    </Text>
                  </Group>
                </Box>

                {/* Top Cost Contributors Section */}
                <Box>
                  <Text fz={14} fw={600} c="#868e96" mb={12}>
                    {TARIFF_CALC_STRINGS.topCostContributors}
                  </Text>
                  <Stack gap="sm">
                    <Box
                      h={7}
                      w="85%"
                      style={{
                        background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                        borderRadius: 1,
                      }}
                    />
                    <Box
                      h={7}
                      w="65%"
                      style={{
                        background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                        borderRadius: 1,
                      }}
                    />
                    <Box
                      h={7}
                      w="45%"
                      style={{
                        background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                        borderRadius: 1,
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid.Col>
        </Grid>

        {/* Top Sourcing Recommendations */}
        <Box
          p="lg"
          bg="white"
          pos="relative"
          mt="lg"
          style={{ borderRadius: 8 }}
        >
          {/* Lock Icon - Top Right */}
          <Box pos="absolute" top={16} style={{ right: 16 }}>
            <IconLock size={20} color={LIGHT_BROWN} />
          </Box>

          {/* Current Sourcing Column */}
          <Box
            display="flex"
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}
          >
            <Box
              style={{
                flex: '0 0 35%',
                minWidth: 0,
              }}
            >
              <Text fz={14} fw={600} c="#212529" mb={16}>
                {TARIFF_CALC_STRINGS.topSourcingRecommendations}
              </Text>
              <Text fz={14} fw={600} c="#868e96" mb={8}>
                {TARIFF_CALC_STRINGS.currentSourcedFrom}
              </Text>
              <Group gap={8} align="center">
                {countryCode && (
                  <CircleFlag countryCode={countryCode} height={20} />
                )}
                <Text fz={18} fw={600} c="#212529">
                  {country}
                </Text>
              </Group>
            </Box>

            {/* Double Arrow Icon */}
            <Box
              display="flex"
              style={{
                flex: '0 0 15%',
                justifyContent: 'center',
                paddingTop: 48,
              }}
            >
              <IconArrowsLeftRight size={28} color={TEXT_ICONS_SECONDARY} />
            </Box>

            {/* Alternative Sourcing Table */}
            <Box
              style={{
                flex: '0 0 45%',
                minWidth: 0,
              }}
            >
              <Table>
                <thead>
                  <tr>
                    <th
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#868e96',
                        paddingBottom: 8,
                        textAlign: 'left',
                      }}
                    >
                      {TARIFF_CALC_STRINGS.alternativeSourcing}
                    </th>
                    <th
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#868e96',
                        paddingBottom: 8,
                        textAlign: 'left',
                      }}
                    >
                      {TARIFF_CALC_STRINGS.estimatedCostImpact}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Group gap={8} align="center">
                        <Box
                          w={20}
                          h={20}
                          bg="#e9ecef"
                          style={{ borderRadius: '50%' }}
                        />
                        <Box
                          h={20}
                          w="85%"
                          style={{
                            background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                            borderRadius: 3,
                          }}
                        />
                      </Group>
                    </td>
                    <td>
                      <Box
                        h={20}
                        w="85%"
                        style={{
                          background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                          borderRadius: 3,
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Group gap={8} align="center">
                        <Box
                          w={20}
                          h={20}
                          bg="#e9ecef"
                          style={{ borderRadius: '50%' }}
                        />
                        <Box
                          h={20}
                          w="85%"
                          style={{
                            background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                            borderRadius: 3,
                          }}
                        />
                      </Group>
                    </td>
                    <td>
                      <Box
                        h={20}
                        w="85%"
                        style={{
                          background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                          borderRadius: 3,
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Group gap={8} align="center">
                        <Box
                          w={20}
                          h={20}
                          bg="#e9ecef"
                          style={{ borderRadius: '50%' }}
                        />
                        <Box
                          h={20}
                          w="85%"
                          style={{
                            background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                            borderRadius: 3,
                          }}
                        />
                      </Group>
                    </td>
                    <td>
                      <Box
                        h={20}
                        w="85%"
                        style={{
                          background: `linear-gradient(to right, #e9ecef, color-mix(in srgb, #e9ecef 20%, transparent))`,
                          borderRadius: 3,
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      </Stack>

      {!isCalculating && (
        <Box
          pos="absolute"
          left={0}
          style={{
            top: 400,
            right: 0,
            bottom: 0,
            backgroundColor: '#F9F6ED99',
            backdropFilter: 'blur(5px)',
            border: '1px solid #ced4da',
            borderRadius: 12,
            padding: 24,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {emailSubmitted ? (
            // Success State - Centered Content
            <Box maw={757} w="100%">
              <Stack gap="md" align="center">
                {/* Title with checkmark */}
                <Box
                  display="flex"
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                  }}
                >
                  <Box
                    w={24}
                    h={24}
                    bg="#40c057"
                    display="flex"
                    style={{
                      borderRadius: '50%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="14"
                      height="11"
                      viewBox="0 0 14 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 5.5L5 9.5L13 1.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                  <Text fz={20} fw={600} lh="28px" c="#212529" ta="center">
                    {TARIFF_CALC_STRINGS.fullReportUnlocked}
                  </Text>
                </Box>

                {/* Body text */}
                <Text
                  fz={16}
                  fw={500}
                  lh="24px"
                  c="black"
                  ta="center"
                  maw="100%"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {TARIFF_CALC_STRINGS.fullReportUnlockedSubtitle}
                </Text>

                {/* Book a Demo Button */}
                <Box w={300} mt={8}>
                  <Button
                    component="a"
                    href="https://calendly.com/harris-muir/muir-demo-request"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onDemoClick}
                    rightSection={<IconSquareArrowRight size={16} />}
                    fullWidth
                    className={classes.bookDemoButtonVariant}
                  >
                    {TARIFF_CALC_STRINGS.expertHelpButtonText}
                  </Button>
                </Box>
              </Stack>
            </Box>
          ) : (
            // Form State - Two Column Layout
            <Grid gutter="sm" h="100%" w="100%">
              {/* Left column - Form */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Box p="sm">
                  <form onSubmit={emailForm.onSubmit(onEmailSubmit)}>
                    <Stack gap="md">
                      <Text fz={20} fw={600} lh="28px" c="#212529">
                        Get Your Complete Tariff Analysis
                      </Text>

                      <Stack gap="sm">
                        <TextInput
                          label="Your Company Email"
                          placeholder=""
                          styles={{
                            label: {
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#212529',
                              marginBottom: 4,
                            },
                            input: {
                              height: 36,
                              fontSize: 16,
                              borderRadius: 8,
                              borderColor: '#ced4da',
                              backgroundColor: 'white',
                            },
                          }}
                          {...emailForm.getInputProps('email')}
                        />
                      </Stack>

                      <Stack gap={4}>
                        <Button
                          type="submit"
                          loading={isSubmittingEmail}
                          fullWidth
                          className={classes.variantSubmitButton}
                        >
                          Build My Full Report
                        </Button>

                        <Button
                          component="a"
                          href="https://calendly.com/harris-muir/muir-demo-request"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onDemoClick}
                          fullWidth
                          variant="outline"
                          rightSection={<IconSquareArrowRight size={16} />}
                          className={classes.demoButtonVariant}
                        >
                          {TARIFF_CALC_STRINGS.expertHelpButtonText}
                        </Button>
                      </Stack>
                    </Stack>
                  </form>
                  <Box>
                    <Text fz={12} fw={400} c={TEXT_ICONS_SECONDARY} ta="left">
                      {TARIFF_CALC_STRINGS.reportDeliveryTimeNotice}
                    </Text>
                  </Box>
                </Box>
              </Grid.Col>

              {/* Right column - Value Props + Image */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Box p="md">
                  <Stack gap="md" align="center">
                    {/* Value Props */}
                    <Stack gap="xs" w="100%">
                      <Box
                        display="flex"
                        p={4}
                        style={{
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <IconCircleCheckFilled
                          size={16}
                          style={{ color: '#b27444', flexShrink: 0 }}
                        />
                        <Text fz={14} fw={600} lh="22px" c="#212529">
                          Automate HTS classification for any item
                        </Text>
                      </Box>

                      <Box
                        display="flex"
                        p={4}
                        style={{
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <IconCircleCheckFilled
                          size={16}
                          style={{ color: '#b27444', flexShrink: 0 }}
                        />
                        <Text fz={14} fw={600} lh="22px" c="#212529">
                          Instantly calculate tariff exposure for any product or
                          BOM
                        </Text>
                      </Box>

                      <Box
                        display="flex"
                        p={4}
                        style={{
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <IconCircleCheckFilled
                          size={16}
                          style={{ color: '#b27444', flexShrink: 0 }}
                        />
                        <Text fz={14} fw={600} lh="22px" c="#212529">
                          Access product intelligence that uncovers savings and
                          sourcing opportunities
                        </Text>
                      </Box>
                    </Stack>

                    {/* Results Image */}
                    <Box className={classes.resultsImage}>
                      <img
                        src={ResultsImageVariant}
                        alt="Product Intelligence"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxWidth: 400,
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Grid.Col>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  )
}
